import { DateTime } from 'luxon';
import Analytics from '../../helpers/Analytics';

export const FORMS_HOME_PREVIEW_LIMIT = 5;

export const ASSIGNED_TO_MYSELF_TAB = 'assignedToMyself';
export const ASSIGNED_TO_OTHERS_TAB = 'assignedToOthers';

export const getAssignedNotStartedCount = ({
  assignedForms = [],
  assignedDrafts = {},
} = {}) => (
  assignedForms.filter((form) => !form.editable && !assignedDrafts[form.id]).length
);

export const getDraftCount = ({
  manual: manualDrafts = {},
  assigned: assignedDrafts = {},
} = {}) => (
  Object.keys(manualDrafts).length + Object.keys(assignedDrafts).length
);

export const getSubmittedFormsCount = (forms = {}, range = []) => {
  if (!range?.[0] || !range?.[1]) return Object.keys(forms).length;
  const [start, end] = range;
  return Object.values(forms).filter((form) => {
    const ts = form?.lastUpdated ?? form?.createdAt;
    return ts >= start && ts <= end;
  }).length;
};

export const countApprovalsThisWeek = (formApprovals = {}) => {
  const weekStart = DateTime.local().startOf('week').toMillis();
  return Object.values(formApprovals).filter((approval) => (
    approval.status !== 'Cancelled'
    && approval.lastUpdated >= weekStart
  )).length;
};

export const countDraftsThisWeek = ({
  manual: manualDrafts = {},
  assigned: assignedDrafts = {},
} = {}) => {
  const weekStart = DateTime.local().startOf('week').toMillis();
  const manualDraftsCount = Object.values(manualDrafts).filter((draft) => (
    draft.lastUpdated >= weekStart
  )).length;
  const assignedDraftsCount = Object.values(assignedDrafts).filter((draft) => (
    draft.lastUpdated >= weekStart
  )).length;
  return manualDraftsCount + assignedDraftsCount;
};

export const countAssignedFormsThisWeek = ({
  drafts: {
    manual: manualDrafts = {},
  } = {},
  assignedForms = [],
} = {}) => {
  const weekStart = DateTime.local().startOf('week').toMillis();
  const manualDraftsCount = Object.values(manualDrafts).filter((draft) => (
    draft.lastUpdated >= weekStart
  )).length;
  const assignedFormsCount = assignedForms.filter((form) => (
    form.lastUpdated >= weekStart
  )).length;
  return manualDraftsCount + assignedFormsCount;
};

export const DRAFT_SOURCE_SELF = 'Self-started';
export const DRAFT_SOURCE_ASSIGNMENT = 'From assignment';

export const DRAFT_FILTER_ALL = 'all';
export const DRAFT_FILTER_SELF = 'self';
export const DRAFT_FILTER_ASSIGNMENT = 'assignment';

export const FORMS_SUBPAGE_KEYS = {
  APPROVE: 'approve',
  ASSIGNED: 'assigned',
  DRAFTS: 'drafts',
  SUBMITTED: 'submitted',
  LOG: 'log',
  MANUAL: 'manual',
  TEMPLATES: 'templates',
  IMPORT: 'import',
};

export const getFormsSubpageKey = (pathname = '') => {
  if (pathname.includes('/approve')) return FORMS_SUBPAGE_KEYS.APPROVE;
  if (pathname.includes('/assigned')) return FORMS_SUBPAGE_KEYS.ASSIGNED;
  if (pathname.includes('/drafts')) return FORMS_SUBPAGE_KEYS.DRAFTS;
  if (pathname.includes('/submitted')) return FORMS_SUBPAGE_KEYS.SUBMITTED;
  if (pathname.includes('/log')) return FORMS_SUBPAGE_KEYS.LOG;
  if (pathname.includes('/manual')) return FORMS_SUBPAGE_KEYS.MANUAL;
  if (pathname.includes('/templates')) return FORMS_SUBPAGE_KEYS.TEMPLATES;
  if (pathname.includes('/import')) return FORMS_SUBPAGE_KEYS.IMPORT;
  return null;
};

export const trackFormsSubpageView = (subpageKey) => {
  switch (subpageKey) {
    case FORMS_SUBPAGE_KEYS.APPROVE:
      Analytics.track('Forms/ViewApprove');
      break;
    case FORMS_SUBPAGE_KEYS.MANUAL:
      Analytics.track('Forms/ViewManual');
      break;
    case FORMS_SUBPAGE_KEYS.LOG:
      Analytics.track('Forms/ViewFormLog');
      break;
    case FORMS_SUBPAGE_KEYS.IMPORT:
      Analytics.track('Forms/ViewImport');
      break;
    case FORMS_SUBPAGE_KEYS.TEMPLATES:
      Analytics.track('Forms/ViewForms');
      break;
    default:
      break;
  }
};

export const getFormsSubpageMeta = ({
  approvalCount = 0,
  t,
} = {}) => ({
  [FORMS_SUBPAGE_KEYS.APPROVE]: {
    title: 'Needs your approval',
    subtitle: `${approvalCount} forms pending — review, approve, or reject in bulk.`,
  },
  [FORMS_SUBPAGE_KEYS.ASSIGNED]: {
    title: 'Assigned',
    subtitle: 'Forms assigned to you or your team that haven\'t been started. Drafts are tagged inline.',
  },
  [FORMS_SUBPAGE_KEYS.DRAFTS]: {
    title: 'Drafts',
    subtitle: 'Every in-progress form in one place — self-started or saved from an assignment.',
  },
  [FORMS_SUBPAGE_KEYS.SUBMITTED]: {
    title: 'Submitted',
    subtitle: `Forms submitted this week across all ${t('Project', { count: 2 }).toLowerCase()} and crews.`,
  },
  [FORMS_SUBPAGE_KEYS.LOG]: {
    title: 'Log',
    subtitle: 'Every form submission across your organization for the selected pay period.',
  },
  [FORMS_SUBPAGE_KEYS.MANUAL]: {
    title: 'Start a new form',
    subtitle: `Pick a workflow from favorites, a ${t('Project').toLowerCase()} library, or the global library.`,
  },
  [FORMS_SUBPAGE_KEYS.TEMPLATES]: {
    title: 'Workflows',
    subtitle: 'Each workflow pairs a form workflow with rules that route, notify, and trigger actions automatically when it\'s submitted.',
  },
  [FORMS_SUBPAGE_KEYS.IMPORT]: {
    title: 'Import forms',
    subtitle: 'Bulk import historical forms from Excel. We\'ll guide you through it.',
  },
});

